import {afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Injector, input, signal, viewChildren} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {distinctUntilChanged, startWith, switchMap} from "rxjs";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {BeslutningSkjemaKomponent} from "../../../beslutning/komponenter/beslutning-skjema/beslutning-skjema";
import {OppgaveSkjemaKomponent} from "../../../oppgave/komponenter/oppgave-skjema/oppgave-skjema";
import {AvstemningMedResultatModell, AvstemningsstatusModell, BeslutningModell, BeslutningSpesifikasjon, BeslutningsSpesifikasjoner, GruppeId, GruppeMedlemListeModell, OppgaveModell, OppgaverSpesifikasjoner, OppgaveSpesifikasjon} from "../../../../../felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erTomListe, nullSikkerListe, siste} from "felles-ng2/støttefunksjoner/lister";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {TranslateModule} from "@ngx-translate/core";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {oppgaveFristSomPlainDateUTC, oppgaveFristSomSerialisertUTC} from "felles-ng2/modell/oppgaveModellHjelpere";
import {beslutningBeskrivelseEllerForslag, harBeslutningsforslagtekst, nullsikkerBeslutningTittelEllerForslag} from "felles-ng2/modell/beslutningModellHjelpere";
import {HtmlFormatertKomponent} from "web-ng2/delt/komponenter/html-formatert/html-formatert";
import {RammeInnholdKomponent, RammeMedTittelKomponent, RammeTittelKomponent} from "web-ng2/delt/komponenter/ramme-med-tittel/ramme-med-tittel";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {MatTooltip} from "@angular/material/tooltip";
import {map} from "rxjs/operators";
import {oppdaterGuiVedEndringIFormArrayLengde} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {GenerertBeslutning, GenerertOppgave} from "felles-ng2/modell/transkripsjonModellHjelpere";


const debug = initDebugger('MøteprotokollRedigerBeslutningerKomponent');

export type SkjemaType = ReturnType<typeof MøteprotokollRedigerBeslutningerKomponent.opprettSkjema>;
export type SkjemaVerdier = SkjemaType['value'];
export type SkjemaRadType = ReturnType<typeof MøteprotokollRedigerBeslutningerKomponent.opprettBeslutningSkjema>;
export type SkjemaRadVerdier = SkjemaRadType['value'];
export type SkjemaOppgaverType = ReturnType<typeof MøteprotokollRedigerBeslutningerKomponent.opprettOppgaverSkjema>;
export type SkjemaOppgaveRadType = ReturnType<typeof MøteprotokollRedigerBeslutningerKomponent.opprettOppgaveSkjema>;
export type SkjemaOppgaveRadVerdier = SkjemaOppgaveRadType['value'];


function beslutningSpesifikasjonSomSkjemaVerdier(gjenopprettetNyBeslutning: BeslutningSpesifikasjon) {
    return {
        id: gjenopprettetNyBeslutning.id,
        tittel: gjenopprettetNyBeslutning.tittel,
        beskrivelse: gjenopprettetNyBeslutning.beskrivelse
    };
}

/**
 * møteprotokoll
 */
@Component({
    selector: 'moeteprotokoll-rediger-beslutninger',
    templateUrl: 'møteprotokoll-rediger-beslutninger.html',
    styleUrl: 'møteprotokoll-rediger-beslutninger.scss',
    host: {'data-testid': 'rediger-beslutninger'},
    imports: [
        BeslutningSkjemaKomponent,
        OppgaveSkjemaKomponent,
        MatIconButton,
        MatIcon,
        MatButton,
        TranslateModule,
        HtmlFormatertKomponent,
        RammeMedTittelKomponent,
        MatTooltip,
        RammeInnholdKomponent,
        RammeTittelKomponent
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteprotokollRedigerBeslutningerKomponent {

    skjema = input.required<SkjemaType>();
    gruppeId = input.required<GruppeId>();

    beslutningElementer = viewChildren<ElementRef<HTMLElement>>('beslutning_element');
    antallForslag = signal(0);

    static opprettSkjema() {
        return new FormGroup({
            eksisterende: new FormArray<SkjemaRadType>([]),
            nye: new FormArray<SkjemaRadType>([])
        })
    }

    /** Oppretter nytt skjema for beslutning.
     * Dersom angitt beslutning er null antar vi at skjemaet representerer en ny beslutning,
     * da vil beslutningstittel være påkrevet.
     * Dersom angitt beslutning er ikke-null antar vi at skjemaet representerer en eksisterende beslutning,
     * da vil enten beslutningstittel eller beskrivelse være utfylt */
    static opprettBeslutningSkjema(beslutning: BeslutningModell | null = null) {
        let skjema = new FormGroup({
            beslutningskjema: BeslutningSkjemaKomponent.opprettSkjema(),
            /** Satt dersom raden representerer en eksisterende beslutning */
            beslutning: new FormControl<BeslutningModell | null>(null, {nonNullable: true}),
            slettet: new FormControl(false, {nonNullable: true}),
            erForslag: new FormControl(false, {nonNullable: true}),
            visForslag: new FormControl(false, {nonNullable: true}),
            oppgaver: MøteprotokollRedigerBeslutningerKomponent.opprettOppgaverSkjema()
        });
        if (erIkkeNil(beslutning)) {
            skjema.patchValue({
                beslutningskjema: {
                    id: beslutning.id,
                    tittel: nullsikkerBeslutningTittelEllerForslag(beslutning),
                    beskrivelse: beslutningBeskrivelseEllerForslag(beslutning)
                },
                beslutning: beslutning,
                erForslag: beslutning.erForslag
            });
            BeslutningSkjemaKomponent.settFelterPåkrevdForEksisterendeBeslutning(skjema.controls.beslutningskjema);
            beslutning.oppgaver.forEach(oppgave =>
              skjema.controls.oppgaver.controls.eksisterende.push(MøteprotokollRedigerBeslutningerKomponent.opprettOppgaveSkjema(oppgave)));
        } else {
            BeslutningSkjemaKomponent.settFelterPåkrevdForNyBeslutning(skjema.controls.beslutningskjema);
        }
        return skjema;
    }

    static gjenopprettBeslutningSkjema(skjema: SkjemaType, skjemaverdier: BeslutningsSpesifikasjoner, tilgjengeligeMedlemmer: GruppeMedlemListeModell[]) {
        skjemaverdier.eksisterendeBeslutninger.forEach(gjenopprettetBeslutning => {
            const eksisterendeBeslutning = skjema.controls.eksisterende.controls.find(kandidat => kandidat.value.beslutning!.id == gjenopprettetBeslutning.id);
            if (erNil(eksisterendeBeslutning)) {
                debug("gjenopprettFraSkjema - finner ikke tilsvarende beslutning") //@todo
                return;
            }
            eksisterendeBeslutning.patchValue({
                slettet: gjenopprettetBeslutning.slettet,
                erForslag: false,
                beslutningskjema: beslutningSpesifikasjonSomSkjemaVerdier(gjenopprettetBeslutning)
            });

            MøteprotokollRedigerBeslutningerKomponent.gjenopprettOppgaveSkjema(eksisterendeBeslutning.controls.oppgaver, gjenopprettetBeslutning.oppgaver, tilgjengeligeMedlemmer);
        });

        skjemaverdier.nyeBeslutninger
          .map(gjenopprettetNyBeslutning => {
              const nyBeslutning = MøteprotokollRedigerBeslutningerKomponent.opprettBeslutningSkjema();
              nyBeslutning.patchValue({beslutningskjema: beslutningSpesifikasjonSomSkjemaVerdier(gjenopprettetNyBeslutning)});
              MøteprotokollRedigerBeslutningerKomponent.gjenopprettOppgaveSkjema(nyBeslutning.controls.oppgaver, gjenopprettetNyBeslutning.oppgaver, tilgjengeligeMedlemmer);
              return nyBeslutning;
          })
          .forEach(nyBeslutning => skjema.controls.nye.insert(0, nyBeslutning));
    }

    static opprettOppgaverSkjema() {
        return new FormGroup({
            eksisterende: new FormArray<SkjemaOppgaveRadType>([]),
            nye: new FormArray<SkjemaOppgaveRadType>([])
        });
    }

    static opprettOppgaveSkjema(oppgave: OppgaveModell | null = null) {
        const skjema = new FormGroup({
            oppgave: OppgaveSkjemaKomponent.opprettSkjema(),
            slettet: new FormControl(false, {nonNullable: true}),
        });
        if (erIkkeNil(oppgave))
            skjema.controls.oppgave.patchValue({
                ...oppgave,
                frist: oppgaveFristSomPlainDateUTC(oppgave)
            });
        return skjema;
    }

    static gjenopprettOppgaveSkjema(skjema: SkjemaOppgaverType, skjemaverdier: OppgaverSpesifikasjoner, tilgjengeligeMedlemmer: GruppeMedlemListeModell[]) {
        skjemaverdier.eksisterendeOppgaver.forEach(gjenopprettetOppgave => {
            const eksisterendeOppgave = skjema.controls.eksisterende.controls.find(kandidat => kandidat.value.oppgave!.id == gjenopprettetOppgave.id);
            if (erNil(eksisterendeOppgave)) {
                debug("gjenopprettFraSkjema - finner ikke tilsvarende oppgave") //@todo
                return;
            }
            eksisterendeOppgave.patchValue({
                oppgave: oppgaveSpesifikasjonSomSkjemaVerdier(gjenopprettetOppgave, tilgjengeligeMedlemmer),
                slettet: gjenopprettetOppgave.slettet
            });

        });
        skjemaverdier.nyeOppgaver
          .map(gjenopprettetNyOppgave => {
              const nyOppgave = MøteprotokollRedigerBeslutningerKomponent.opprettOppgaveSkjema();
              nyOppgave.patchValue({oppgave: oppgaveSpesifikasjonSomSkjemaVerdier(gjenopprettetNyOppgave, tilgjengeligeMedlemmer)});
              return nyOppgave;
          })
          .forEach(nyOppgave => skjema.controls.nye.insert(0, nyOppgave));
    }

    static somSpesifikasjoner(verdier: SkjemaVerdier): BeslutningsSpesifikasjoner {
        return {
            //dersom alle innslagene i FormArray er disabled vil også FormArray være disabled, og da returnerer FormArray.value undefined
            eksisterendeBeslutninger: nullSikkerListe(verdier.eksisterende)
              .filter(beslutning => !beslutning.erForslag)
              .map(beslutning => beslutningSomSpesifikasjon(beslutning)),
            nyeBeslutninger: nullSikkerListe(verdier.nye)
              .filter(beslutning => erIkkeTomRad(beslutning))
              .map(beslutning => beslutningSomSpesifikasjon(beslutning))
        };
    }

    constructor(private oversettelser: Oversettelser, private injector: Injector, cdRef: ChangeDetectorRef) {
        const skjemaSomObservable = toObservable(this.skjema);
        const eksisterendeSomObservable = skjemaSomObservable
          .pipe(switchMap(skjema => skjema.controls.eksisterende.valueChanges.pipe(startWith(skjema.value.eksisterende))));

        //FormArray.push (og øvrige metoder på FormArray) trigger ikke rendering, trigger derfor her manuelt oppdatering av GUI når antall beslutninger endres
        //dette er nødvendig da det å legge til beslutninger ikke alltid skjer som direkte følge av bruker-interaksjon, feks ifbm gjenopprettBeslutningSkjema
        //har ikke lagt til eksplisitt håndtering av at oppgave legges til da dette skjer samtidig med at beslutning legges til
        oppdaterGuiVedEndringIFormArrayLengde(skjemaSomObservable.pipe(map(skjema => skjema.controls.eksisterende)), cdRef);
        oppdaterGuiVedEndringIFormArrayLengde(skjemaSomObservable.pipe(map(skjema => skjema.controls.nye)), cdRef);

        //oppdater antallForslag ved endring i beslutning.erForslag
        eksisterendeSomObservable
          .pipe(
            map(beslutninger => beslutninger!.filter(beslutning => beslutning.erForslag).length),
            distinctUntilChanged(),
            takeUntilDestroyed())
          .subscribe(antallForslag => this.antallForslag.set(antallForslag));

        //sørger for å vise tom oppgave-rad under eksisterende beslutninger
        eksisterendeSomObservable
          .pipe(takeUntilDestroyed())
          .subscribe(eksisterende => this.vedEndringIEksisterendeBeslutninger(eksisterende!));

        //sørger for at det vises en tom beslutning-rad under nye beslutninger initielt
        skjemaSomObservable
          .pipe(takeUntilDestroyed())
          .subscribe(skjema => this.leggTilNyBeslutning());

        //sørger for å legge til ny tom beslutning-rad under nye beslutninger når siste rad fylles ut
        skjemaSomObservable
          .pipe(
            switchMap(skjema => skjema.controls.nye.valueChanges),
            takeUntilDestroyed())
          .subscribe(nye => this.vedEndringINyeBeslutninger(nye));
    }

    slettNyBeslutning(indeks: number) {
        this.skjema().controls.nye.removeAt(indeks);
    }

    slettEksisterendeBeslutning(beslutning: SkjemaRadType) {
        if (beslutning.value.beslutning!.erForslag) {
            //beslutningen er opprettet fra et forslag - da endrer vi tilbake til forslag
            beslutning.patchValue({
                beslutningskjema: {
                    tittel: beslutning.value.beslutning!.tittelForslag,
                    beskrivelse: beslutning.value.beslutning!.beskrivelseForslag
                },
                erForslag: true //listene over forslag/endelige beslutninger oppdateres via valueChanges lytter på erForslag
            });
        } else {
            beslutning.controls.slettet.setValue(true);
            beslutning.disable();
        }
    }

    angreSlettEksisterendeBeslutning(beslutning: SkjemaRadType) {
        beslutning.controls.slettet.setValue(false);
        beslutning.enable();
    }

    slettNyOppgave(beslutning: SkjemaRadType, indeks: number) {
        beslutning.controls.oppgaver.controls.nye.removeAt(indeks);
    }

    slettEksisterendeOppgave(oppgave: SkjemaOppgaveRadType) {
        oppgave.controls.slettet.setValue(true);
        oppgave.disable();
    }

    angreSlettEksisterendeOppgave(oppgave: SkjemaOppgaveRadType) {
        oppgave.controls.slettet.setValue(false);
        oppgave.enable();
    }

    registrerForslagSomBeslutning(forslag: SkjemaRadType) {
        forslag.patchValue({
            erForslag: false //listene over forslag/endelige beslutninger oppdateres via valueChanges lytter på erForslag
        });
        this.puttMarkørIBeslutningTittelfelt(forslag);
    }

    opprettBeslutningFraAvstemning(avstemning: AvstemningMedResultatModell, avstemningsstatus: AvstemningsstatusModell): SkjemaRadType {
        const beslutningSkjema = this.leggTilNyBeslutning();
        BeslutningSkjemaKomponent.populerSkjemaFraAvstemning(beslutningSkjema.controls.beslutningskjema, avstemning, avstemningsstatus, this.oversettelser);
        this.puttMarkørIBeslutningTittelfelt(beslutningSkjema);
        return beslutningSkjema;
    }

    /**
     * Legger beslutningen fra oppsummeringen av møteopptaket inn som en ny beslutning, med oppgavene sine.
     *
     * Oppsummeringen gir ingen beslutningstittel, så markøren settes i tittelfeltet – bruker må
     * navngi beslutningen selv før protokollen kan lagres.
     */
    opprettBeslutningFraMøteopptak(generertBeslutning: GenerertBeslutning): SkjemaRadType {
        const beslutningSkjema = this.leggTilNyBeslutning();
        beslutningSkjema.patchValue({beslutningskjema: {tittel: generertBeslutning.tittel , beskrivelse: generertBeslutning.beskrivelse}});
        generertBeslutning.oppgaver.forEach(oppgave => this.opprettOppgaveFraMøteopptak(beslutningSkjema, oppgave));
        this.puttMarkørIBeslutningTittelfelt(beslutningSkjema);
        return beslutningSkjema;
    }

    /**
     * Legger oppgaven fra oppsummeringen av møteopptaket inn som en ny oppgave under angitt beslutning.
     *
     * Oppgaven fyller ut den tomme raden som ligger sist under beslutningen – på samme måte som når
     * bruker skriver i den selv – slik at oppgavene ikke havner bak en tom rad.
     */
    opprettOppgaveFraMøteopptak(beslutning: SkjemaRadType, generertOppgave: GenerertOppgave): SkjemaOppgaveRadType {
        const oppgaveSkjema = this.tomOppgaverad(beslutning) ?? this.leggTilNyOppgave(beslutning);
        oppgaveSkjema.controls.oppgave.patchValue({
            tittel: generertOppgave.tittel,
            beskrivelse: generertOppgave.beskrivelse,
            ansvarlig: generertOppgave.ansvarlig,
            frist: generertOppgave.frist
        });
        //raden vi fylte ut var den tomme "legg til"-raden, sørg for at det igjen finnes en tom rad sist
        this.leggTilNyOppgaveDersomSisteRadIkkeTom(beslutning);
        return oppgaveSkjema;
    }

    harForslagstekster(beslutning: SkjemaRadType) {
        return harBeslutningsforslagtekst(beslutning.value.beslutning!);
    }

    visForslagHjelpetekst(beslutning: SkjemaRadType) {
        return this.harForslagstekster(beslutning) ?
          this.oversettelser.oversett(i18n('Klikk for å vise beslutningsforslaget registrert på denne beslutningen')) :
          this.oversettelser.oversett(i18n('Ingen forslagtekst er registrert på denne beslutningen'));
    }

    visForslag(beslutning: SkjemaRadType) {
        return beslutning.controls.visForslag.setValue(true);
    }

    skjulForslag(beslutning: SkjemaRadType) {
        return beslutning.controls.visForslag.setValue(false);
    }

    //private hjelpemetoder

    /** Sørger for å lytte til endringer i oppgaver knyttet til eksisterende beslutning. Legger til ny oppgaverad når siste rad fylles ut. */
    private vedEndringIEksisterendeBeslutninger(beslutninger: SkjemaRadVerdier[]) {
        //håndter oppgaveliste
        this.vedEndringIOppgaver(this.skjema().controls.eksisterende);
    }

    /** Legger til ny beslutningrad når siste rad fylles ut. Lytter til endringer i oppgaver knyttet til nye beslutning. Legger til ny oppgaverad når siste rad fylles ut. */
    private vedEndringINyeBeslutninger(beslutninger: SkjemaRadVerdier[]) {
        //sørger for at det alltid vises en tom rad til slutt i listen
        this.leggTilNyBeslutningDersomSisteRadIkkeTom(beslutninger);

        //sørger for at det ikke vises mer enn en tom rad i slutten av listen
        if (beslutninger.slice(-2).filter(rad => erTomBeslutningsrad(rad)).length > 1) //sann dersom de to siste radene er tomme
          // fjerner nest siste rad (ikke siste - denne fungere som "trigger" for nye rader, dessuten måtte vi har fjernet required-validering som ligger nest siste om vi fjerner siste...)
            this.skjema().controls.nye.removeAt(this.skjema().controls.nye.length - 2);

        //har valgt å ikke fjerne tomme rader etterfulgt av ikke-tomme rader - disse må bruker selv slette

        //håndter oppgaveliste
        this.vedEndringIOppgaver(this.skjema().controls.nye);
    }

    /** Legger til ny oppgaverad når siste rad fylles ut. */
    private vedEndringIOppgaver(beslutninger: FormArray<SkjemaRadType>) {
        beslutninger.controls.forEach(beslutning => {
            this.leggTilNyOppgaveDersomSisteRadIkkeTom(beslutning);

            //sørger for at det ikke vises mer enn en tom oppgave i slutten av listen
            const oppgaver = beslutning.value.oppgaver!.nye!;
            if (oppgaver.slice(-2).filter(oppgave => erTomOppgaveRad(oppgave)).length > 1) { //sann dersom de to siste radene er tomme
                // fjerner nest siste rad (ikke siste - denne fungere som "trigger" for nye rader, dessuten måtte vi har fjernet required-validering som ligger nest siste om vi fjerner siste...)
                beslutning.controls.oppgaver.controls.nye.removeAt(oppgaver.length - 2);
            }
        });
    }

    private leggTilNyBeslutningDersomSisteRadIkkeTom(beslutninger: SkjemaRadVerdier[]) {
        if (erTomListe(beslutninger) || erIkkeTomRad(siste(beslutninger)))
            this.leggTilNyBeslutning();
    }

    private leggTilNyOppgaveDersomSisteRadIkkeTom(beslutning: SkjemaRadType) {
        const oppgaver = beslutning.value.oppgaver!.nye!;
        if (erTomListe(oppgaver) || erIkkeTomOppgaveRad(siste(oppgaver)))
            this.leggTilNyOppgave(beslutning);
    }

    /** Legger til ny beslutning sist i listen over nye beslutninger */
    private leggTilNyBeslutning(): SkjemaRadType {
        this.skjema().controls.nye.controls.forEach(rad => BeslutningSkjemaKomponent.settFelterPåkrevdForNyBeslutning(rad.controls.beslutningskjema));
        const nyBeslutning = MøteprotokollRedigerBeslutningerKomponent.opprettBeslutningSkjema();
        //siste nye beslutning skal ikke ha validering (det skal alltid være et tom siste innslag i listen)
        BeslutningSkjemaKomponent.settFelterPåkrevdForNyBeslutning(nyBeslutning.controls.beslutningskjema, false);
        this.leggTilNyOppgave(nyBeslutning);
        this.skjema().controls.nye.push(nyBeslutning);
        return nyBeslutning;
    }

    /** Den tomme raden som ligger sist under beslutningen for registrering av ny oppgave, null når siste rad er utfylt */
    private tomOppgaverad(beslutning: SkjemaRadType): SkjemaOppgaveRadType | null {
        const sisteRad = siste(beslutning.controls.oppgaver.controls.nye.controls);
        return erIkkeNil(sisteRad) && erTomOppgaveRad(sisteRad.value) ? sisteRad : null;
    }

    /** Legger til en tom oppgaverad sist under angitt beslutning, og returnerer den nye raden */
    private leggTilNyOppgave(beslutning: SkjemaRadType): SkjemaOppgaveRadType {
        beslutning.controls.oppgaver.controls.nye.controls.forEach(rad => {
            OppgaveSkjemaKomponent.settFelterPåkrevd(rad.controls.oppgave);
            //av ukjent grunn får ikke validator lagt til på ansvarlig effekt før reset kalles på feltet..
            if (erNil(rad.value.oppgave!.ansvarlig)) rad.controls.oppgave.controls.ansvarlig.reset(null, {onlySelf: true});
        });
        const nyOppgave = MøteprotokollRedigerBeslutningerKomponent.opprettOppgaveSkjema();
        OppgaveSkjemaKomponent.settFelterPåkrevd(nyOppgave.controls.oppgave, false);
        beslutning.controls.oppgaver.controls.nye.push(nyOppgave);
        return nyOppgave;
    }

    private puttMarkørIBeslutningTittelfelt(forslag: SkjemaRadType) {
        afterNextRender({
            read: () => {
                let beslutningElement = this.beslutningElementer().find(beslutning => beslutning.nativeElement.getAttribute('data-beslutning-id') == forslag.value.beslutningskjema!.id);
                if (erNil(beslutningElement)) return;
                const tittelInputfelt: HTMLInputElement = beslutningElement.nativeElement.querySelector('beslutning-skjema .tittel input')!;
                tittelInputfelt.focus();
            }
        }, {injector: this.injector});
    }
}

function erIkkeTomRad(rad: SkjemaRadVerdier | null) {
    return !erTomBeslutningsrad(rad);
}

/**
 * Sann når beslutningsraden ikke er utfylt. Lista over nye beslutninger har alltid en slik rad sist,
 * som bruker registrerer neste beslutning i.
 */
export function erTomBeslutningsrad(rad: SkjemaRadVerdier | null) {
    return erTomEllerNil(rad?.beslutningskjema!.tittel) && erTomEllerNil(rad?.beslutningskjema!.beskrivelse);
}


function erIkkeTomOppgaveRad(rad: SkjemaOppgaveRadVerdier | null) {
    return !erTomOppgaveRad(rad);
}

function erTomOppgaveRad(rad: SkjemaOppgaveRadVerdier | null) {
    return erTomEllerNil(rad?.oppgave?.tittel) && erTomEllerNil(rad?.oppgave?.beskrivelse);
}

function beslutningSomSpesifikasjon(beslutning: SkjemaRadVerdier): BeslutningSpesifikasjon {
    return {
        id: beslutning.beslutningskjema!.id!,
        slettet: beslutning.slettet!,
        tittel: beslutning.beslutningskjema!.tittel!,
        beskrivelse: beslutning.beslutningskjema!.beskrivelse!,
        oppgaver: {
            //dersom alle innslagene i FormArray er disabled vil også FormArray være disabled, og da returnerer FormArray.value undefined
            eksisterendeOppgaver: nullSikkerListe(beslutning.oppgaver!.eksisterende).map(oppgave => oppgaveSomSpesifikasjon(oppgave)),
            nyeOppgaver: nullSikkerListe(beslutning.oppgaver!.nye)
              .filter(oppgave => !erTomOppgaveRad(oppgave))
              .map(oppgave => oppgaveSomSpesifikasjon(oppgave))
        }
    };
}

function oppgaveSomSpesifikasjon(oppgave: SkjemaOppgaveRadVerdier): OppgaveSpesifikasjon {
    return {
        id: oppgave.oppgave!.id!,
        slettet: oppgave.slettet!,
        tittel: oppgave.slettet ? '-' : oppgave.oppgave!.tittel!, // Når oppgave er slettet, setter vi '-' i stedet for tittel, serveren forventer ikke-tom tittel #2194
        beskrivelse: oppgave.oppgave!.beskrivelse ?? null,
        // @ts-ignore //ifbm skjemalås ved protokoll-redigering må vi tillate at ansvarlig ikke er satt
        ansvarligId: oppgave.oppgave!.ansvarlig?.id ?? null,
        frist: oppgaveFristSomSerialisertUTC(oppgave.oppgave!.frist ?? null)
    }
}

function oppgaveSpesifikasjonSomSkjemaVerdier(gjenopprettetNyOppgave: OppgaveSpesifikasjon, tilgjengeligeMedlemmer: GruppeMedlemListeModell[]) {
    return {
        id: gjenopprettetNyOppgave.id,
        tittel: gjenopprettetNyOppgave.tittel,
        beskrivelse: gjenopprettetNyOppgave.beskrivelse,
        frist: oppgaveFristSomPlainDateUTC(gjenopprettetNyOppgave),
        ansvarlig: tilgjengeligeMedlemmer.find(medlem => medlem.id == gjenopprettetNyOppgave.ansvarligId),
    };
}
